// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type ShareLinkScope = "view" | "download";
export const ShareLinkScopeSchema = z.enum(["view", "download"] as const);

export type ShareLinkStatus = "active" | "expired" | "revoked";
export const ShareLinkStatusSchema = z.enum(["active", "expired", "revoked"] as const);

export type ShareLinkExpiryDays = 7 | 30 | 90;
export const ShareLinkExpiryDaysSchema = z.union([z.literal(7), z.literal(30), z.literal(90)]);

export type ShareLink = {
  _id: string;
  reportId: string;
  reportVersion: number;
  scope: Array<ShareLinkScope>;
  createdBy: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  lastOpenedAt: string | null;
  openCount: number;
  status: ShareLinkStatus;
};
export const ShareLinkSchema = z.object({
  _id: z.string(),
  reportId: z.string(),
  reportVersion: z.number().int(),
  scope: z.array(ShareLinkScopeSchema),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  lastOpenedAt: z.string().nullable(),
  openCount: z.number().int(),
  status: ShareLinkStatusSchema,
}).strict();

export type CreateShareLinkBody = {
  scope?: Array<ShareLinkScope> | undefined;
  expiryDays: ShareLinkExpiryDays;
};
export const CreateShareLinkBodySchema = z.object({
  scope: z.array(ShareLinkScopeSchema).default(["view"]),
  expiryDays: ShareLinkExpiryDaysSchema,
}).strict().refine((b) => b.scope.includes('view'));

export type CreateShareLinkResult = {
  shareLink: ShareLink;
  url: string;
};
export const CreateShareLinkResultSchema = z.object({
  shareLink: ShareLinkSchema,
  url: z.string(),
}).strict();

export type RevokeShareLinkResult = {
  shareLink: ShareLink;
};
export const RevokeShareLinkResultSchema = z.object({
  shareLink: ShareLinkSchema,
}).strict();

export type SharedReportView = {
  reportVersion: number;
  scope: Array<ShareLinkScope>;
  generatedAt: string | null;
  content: {
  title: string;
  accentColor: string | null;
  headerStyle: "standard" | "centered" | "minimal";
  style: {
  logo: string | null;
  accentColor: string | null;
  font: "sans" | "serif" | "mono";
  sectionHeaderStyle: "filled" | "centered" | "minimal";
  thumbnailSize: "small" | "medium" | "large";
  pageSize: "A4" | "letter";
  coverPage: boolean;
  headingScale: "sm" | "md" | "lg";
  secondaryTextFromAccent: boolean;
  sectionUnderlineWidth: "full" | "text";
  surfaceRadius: "rounded" | "sharp";
  pageOrientation: "portrait" | "landscape";
  marginsPreset: "narrow" | "normal" | "wide";
  letterhead: {
  logoLeft: {
  enabled: boolean;
  content: string | null;
};
  logoCenter: {
  enabled: boolean;
  content: string | null;
};
  logoRight: {
  enabled: boolean;
  content: string | null;
};
  title: {
  enabled: boolean;
  content: string | null;
};
  addressBlock: {
  enabled: boolean;
  content: string | null;
};
  footerPageNumber: {
  enabled: boolean;
  content: string | null;
};
  footerText: {
  enabled: boolean;
  content: string | null;
};
  mode: "none" | "firstContentPage" | "everyContentPage";
  layout: "standard" | "compact";
  confidentiality: "none" | "confidential" | "internal" | "restricted";
  textColor: string | null;
};
  typographyRoles: {
  caption: "sm" | "md";
};
  colorRoles: {
  brand: "accent" | "neutral";
  heading: "default" | "brand";
  border: "default" | "brand";
  findingSeverity: "standard" | "brand";
};
  borderDensity: "standard" | "bold";
  variants: {
  cover: "standard" | "accent";
  letterhead: "standard" | "divider";
  table: "standard" | "striped";
  finding: "standard" | "compact";
};
};
  layoutPolicy: {
  pageFormat: "A4" | "letter";
  orientation: "portrait" | "landscape";
  marginsMm: {
  top: number;
  right: number;
  bottom: number;
  left: number;
};
  frontMatter: {
  enabled: boolean;
  countsTowardPageNumbers: boolean;
};
  letterhead: {
  mode: "none" | "firstContentPage" | "everyContentPage";
};
};
  templateName: string | null;
  templateVersion: number | null;
  subjectName: string | null;
  subjectAddress: string | null;
  typeName: string | null;
  inspectionStatus: string | null;
  inspectionDate: string | null;
  generatedAt: string | null;
  reportReference: string | null;
  branding: {
  organizationName: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  font: "outfit" | "system" | "serif" | null;
} | null;
  sections: Array<{
  key: string;
  heading: string;
  kind: "section" | "page" | "table";
  pageBreak: "auto" | "always" | "avoid";
  presentation: {
  layout: "list" | "groupedList" | "table" | "summaryCards" | "narrative";
  columns: "one" | "two";
  density: "comfortable" | "compact" | "condensed";
  heading: "visible" | "hidden" | "renamed" | "numbered";
  headingLabel: string | null;
  repeatHeading: boolean;
  showEmpty: "show" | "hide" | "notProvided";
  evidencePlacement: "inline" | "afterResponse" | "gallery" | "appendix";
  findingEmphasis: "standard" | "highlighted" | "summaryOnly";
  tableColumns: Array<{
  key: string;
  visible: boolean;
  emphasis: "standard" | "primary" | "secondary" | "compact";
}> | null;
  overflow: "wrap" | "truncate";
  showTotalsRow: boolean;
};
  tocKey: string | null;
  body: string | null;
  rows: Array<{
  label: string;
  value: string;
  tone: "neutral" | "positive" | "warning" | "critical";
  kind: "text" | "photo-grid" | "chip";
  photos: Array<{
  thumbnailUrl: string | null;
  caption: string | null;
}> | null;
  chip: {
  label: string;
  color: string | null;
} | null;
  description: string | null;
}>;
  columns: Array<string> | null;
  columnKeys: Array<string> | null;
  tableRows: Array<Array<{
  kind: "text" | "photo-grid" | "chip";
  value: string;
  photos: Array<{
  thumbnailUrl: string | null;
  caption: string | null;
}> | null;
  chip: {
  label: string;
  color: string | null;
} | null;
}>> | null;
}>;
  scoreSummary: {
  earned: number;
  possible: number;
  percentage: number | null;
} | null;
  flaggedItems: {
  total: number;
  bySeverity: {
  low: number;
  medium: number;
  high: number;
  critical: number;
};
  items: Array<{
  observationText: string;
  recommendationText: string | null;
  severityTag: string | null;
  evidenceCount: number;
}>;
} | null;
  actions: {
  total: number;
  items: Array<{
  observationText: string;
  recommendationText: string;
  severityTag: string | null;
}>;
} | null;
  tableOfContents: Array<{
  key: string;
  label: string;
  page?: number | null | undefined;
}>;
  disclaimer: string | null;
  methodology: string | null;
  mediaSummary: {
  totalPhotos: number;
  roomsWithPhotos: number;
  photos: Array<{
  roomName: string | null;
  thumbnailUrl: string | null;
  caption: string | null;
  fieldLabel: string | null;
  sectionName: string | null;
  capturedAt: string | null;
}>;
} | null;
  mediaSummaryPresentation: {
  layout: "grid" | "list" | "fullWidth" | "appendix";
  imageFit: "cover" | "contain";
  showCaptions: boolean;
  showTimestamps: boolean;
  showLocation: boolean;
  showAnnotations: boolean;
  groupBy: "none" | "question" | "section" | "finding";
};
  contentToggles: {
  footer: boolean;
  tableOfContents: boolean;
  pageBreak: boolean;
  summary: boolean;
  flaggedItems: boolean;
  actions: boolean;
  disclaimer: boolean;
  inspectionResults: boolean;
  tables: boolean;
  mediaSummary: boolean;
  scores: boolean;
  methodology: boolean;
  showQuestionLabels: boolean;
  showAnswers: boolean;
  showDescriptions: boolean;
  metadata: boolean;
  includeInternalNotes: boolean;
  evidenceCaptions: boolean;
};
  findingsGroupBy: "severity" | "section" | "status" | "assignee";
};
  artifact: "none" | "running" | "failed" | "ready" | "expired";
  downloadUrl: string | null;
};
export const SharedReportViewSchema = z.object({
  reportVersion: z.number().int(),
  scope: z.array(ShareLinkScopeSchema),
  generatedAt: z.string().nullable(),
  content: z.object({
    title: z.string(),
    accentColor: z.string().nullable(),
    headerStyle: z.enum(["standard", "centered", "minimal"] as const).default("standard"),
    style: z.object({
      logo: z.string().nullable(),
      accentColor: z.string().nullable(),
      font: z.enum(["sans", "serif", "mono"] as const).default("sans"),
      sectionHeaderStyle: z.enum(["filled", "centered", "minimal"] as const).default("filled"),
      thumbnailSize: z.enum(["small", "medium", "large"] as const).default("medium"),
      pageSize: z.enum(["A4", "letter"] as const).default("A4"),
      coverPage: z.boolean().default(false),
      headingScale: z.enum(["sm", "md", "lg"] as const).default("md"),
      secondaryTextFromAccent: z.boolean().default(false),
      sectionUnderlineWidth: z.enum(["full", "text"] as const).default("full"),
      surfaceRadius: z.enum(["rounded", "sharp"] as const).default("rounded"),
      pageOrientation: z.enum(["portrait", "landscape"] as const).default("portrait"),
      marginsPreset: z.enum(["narrow", "normal", "wide"] as const).default("normal"),
      letterhead: z.object({
        logoLeft: z.object({
          enabled: z.boolean().default(false),
          content: z.string().nullable().default(null),
        }).strict(),
        logoCenter: z.object({
          enabled: z.boolean().default(false),
          content: z.string().nullable().default(null),
        }).strict(),
        logoRight: z.object({
          enabled: z.boolean().default(false),
          content: z.string().nullable().default(null),
        }).strict(),
        title: z.object({
          enabled: z.boolean().default(false),
          content: z.string().nullable().default(null),
        }).strict(),
        addressBlock: z.object({
          enabled: z.boolean().default(false),
          content: z.string().nullable().default(null),
        }).strict(),
        footerPageNumber: z.object({
          enabled: z.boolean().default(false),
          content: z.string().nullable().default(null),
        }).strict(),
        footerText: z.object({
          enabled: z.boolean().default(false),
          content: z.string().nullable().default(null),
        }).strict(),
        mode: z.enum(["none", "firstContentPage", "everyContentPage"] as const).default("firstContentPage"),
        layout: z.enum(["standard", "compact"] as const).default("standard"),
        confidentiality: z.enum(["none", "confidential", "internal", "restricted"] as const).default("none"),
        textColor: z.string().nullable().default(null),
      }).strict(),
      typographyRoles: z.object({
        caption: z.enum(["sm", "md"] as const).default("sm"),
      }).strict().default({"caption":"sm"}),
      colorRoles: z.object({
        brand: z.enum(["accent", "neutral"] as const).default("accent"),
        heading: z.enum(["default", "brand"] as const).default("default"),
        border: z.enum(["default", "brand"] as const).default("default"),
        findingSeverity: z.enum(["standard", "brand"] as const).default("standard"),
      }).strict().default({"brand":"accent","heading":"default","border":"default","findingSeverity":"standard"}),
      borderDensity: z.enum(["standard", "bold"] as const).default("standard"),
      variants: z.object({
        cover: z.enum(["standard", "accent"] as const).default("standard"),
        letterhead: z.enum(["standard", "divider"] as const).default("standard"),
        table: z.enum(["standard", "striped"] as const).default("standard"),
        finding: z.enum(["standard", "compact"] as const).default("standard"),
      }).strict().default({"cover":"standard","letterhead":"standard","table":"standard","finding":"standard"}),
    }).strict(),
    layoutPolicy: z.object({
      pageFormat: z.enum(["A4", "letter"] as const),
      orientation: z.enum(["portrait", "landscape"] as const),
      marginsMm: z.object({
        top: z.number(),
        right: z.number(),
        bottom: z.number(),
        left: z.number(),
      }).strict(),
      frontMatter: z.object({
        enabled: z.boolean(),
        countsTowardPageNumbers: z.boolean(),
      }).strict(),
      letterhead: z.object({
        mode: z.enum(["none", "firstContentPage", "everyContentPage"] as const),
      }).strict(),
    }).strict(),
    templateName: z.string().nullable(),
    templateVersion: z.number().int().nullable(),
    subjectName: z.string().nullable(),
    subjectAddress: z.string().nullable(),
    typeName: z.string().nullable(),
    inspectionStatus: z.string().nullable(),
    inspectionDate: z.string().nullable(),
    generatedAt: z.string().nullable(),
    reportReference: z.string().nullable(),
    branding: z.object({
      organizationName: z.string().nullable(),
      logoUrl: z.string().nullable(),
      primaryColor: z.string().nullable(),
      accentColor: z.string().nullable(),
      font: z.enum(["outfit", "system", "serif"] as const).nullable(),
    }).strict().nullable(),
    sections: z.array(z.object({
      key: z.string(),
      heading: z.string(),
      kind: z.enum(["section", "page", "table"] as const).default("section"),
      pageBreak: z.enum(["auto", "always", "avoid"] as const).default("auto"),
      presentation: z.object({
        layout: z.enum(["list", "groupedList", "table", "summaryCards", "narrative"] as const).default("list"),
        columns: z.enum(["one", "two"] as const).default("one"),
        density: z.enum(["comfortable", "compact", "condensed"] as const).default("comfortable"),
        heading: z.enum(["visible", "hidden", "renamed", "numbered"] as const).default("visible"),
        headingLabel: z.string().nullable().default(null),
        repeatHeading: z.boolean().default(false),
        showEmpty: z.enum(["show", "hide", "notProvided"] as const).default("show"),
        evidencePlacement: z.enum(["inline", "afterResponse", "gallery", "appendix"] as const).default("inline"),
        findingEmphasis: z.enum(["standard", "highlighted", "summaryOnly"] as const).default("standard"),
        tableColumns: z.array(z.object({
          key: z.string(),
          visible: z.boolean().default(true),
          emphasis: z.enum(["standard", "primary", "secondary", "compact"] as const).default("standard"),
        }).strict()).nullable().default(null),
        overflow: z.enum(["wrap", "truncate"] as const).default("wrap"),
        showTotalsRow: z.boolean().default(false),
      }).strict(),
      tocKey: z.string().nullable(),
      body: z.string().nullable(),
      rows: z.array(z.object({
        label: z.string(),
        value: z.string(),
        tone: z.enum(["neutral", "positive", "warning", "critical"] as const).default("neutral"),
        kind: z.enum(["text", "photo-grid", "chip"] as const).default("text"),
        photos: z.array(z.object({
          thumbnailUrl: z.string().nullable(),
          caption: z.string().nullable(),
        }).strict()).nullable(),
        chip: z.object({
          label: z.string(),
          color: z.string().nullable(),
        }).strict().nullable(),
        description: z.string().nullable(),
      }).strict()),
      columns: z.array(z.string()).nullable(),
      columnKeys: z.array(z.string()).nullable(),
      tableRows: z.array(z.array(z.object({
        kind: z.enum(["text", "photo-grid", "chip"] as const).default("text"),
        value: z.string(),
        photos: z.array(z.object({
          thumbnailUrl: z.string().nullable(),
          caption: z.string().nullable(),
        }).strict()).nullable(),
        chip: z.object({
          label: z.string(),
          color: z.string().nullable(),
        }).strict().nullable(),
      }).strict())).nullable(),
    }).strict()),
    scoreSummary: z.object({
      earned: z.number(),
      possible: z.number(),
      percentage: z.number().nullable(),
    }).strict().nullable(),
    flaggedItems: z.object({
      total: z.number().int(),
      bySeverity: z.object({
        low: z.number().int(),
        medium: z.number().int(),
        high: z.number().int(),
        critical: z.number().int(),
      }).strict(),
      items: z.array(z.object({
        observationText: z.string(),
        recommendationText: z.string().nullable(),
        severityTag: z.string().nullable(),
        evidenceCount: z.number().int(),
      }).strict()),
    }).strict().nullable(),
    actions: z.object({
      total: z.number().int(),
      items: z.array(z.object({
        observationText: z.string(),
        recommendationText: z.string(),
        severityTag: z.string().nullable(),
      }).strict()),
    }).strict().nullable(),
    tableOfContents: z.array(z.object({
      key: z.string(),
      label: z.string(),
      page: z.number().int().nullable().optional(),
    }).strict()),
    disclaimer: z.string().nullable(),
    methodology: z.string().nullable(),
    mediaSummary: z.object({
      totalPhotos: z.number().int(),
      roomsWithPhotos: z.number().int(),
      photos: z.array(z.object({
        roomName: z.string().nullable(),
        thumbnailUrl: z.string().nullable(),
        caption: z.string().nullable(),
        fieldLabel: z.string().nullable(),
        sectionName: z.string().nullable(),
        capturedAt: z.string().nullable(),
      }).strict()),
    }).strict().nullable(),
    mediaSummaryPresentation: z.object({
      layout: z.enum(["grid", "list", "fullWidth", "appendix"] as const).default("grid"),
      imageFit: z.enum(["cover", "contain"] as const).default("cover"),
      showCaptions: z.boolean().default(true),
      showTimestamps: z.boolean().default(false),
      showLocation: z.boolean().default(false),
      showAnnotations: z.boolean().default(false),
      groupBy: z.enum(["none", "question", "section", "finding"] as const).default("none"),
    }).strict(),
    contentToggles: z.object({
      footer: z.boolean().default(true),
      tableOfContents: z.boolean().default(false),
      pageBreak: z.boolean().default(true),
      summary: z.boolean().default(false),
      flaggedItems: z.boolean().default(true),
      actions: z.boolean().default(true),
      disclaimer: z.boolean().default(false),
      inspectionResults: z.boolean().default(true),
      tables: z.boolean().default(true),
      mediaSummary: z.boolean().default(true),
      scores: z.boolean().default(true),
      methodology: z.boolean().default(false),
      showQuestionLabels: z.boolean().default(true),
      showAnswers: z.boolean().default(true),
      showDescriptions: z.boolean().default(false),
      metadata: z.boolean().default(false),
      includeInternalNotes: z.boolean().default(false),
      evidenceCaptions: z.boolean().default(false),
    }).strict(),
    findingsGroupBy: z.enum(["severity", "section", "status", "assignee"] as const),
  }).strict(),
  artifact: z.enum(["none", "running", "failed", "ready", "expired"] as const),
  downloadUrl: z.string().nullable(),
}).strict();

export type CreateShareLinkResponse = {
  data: CreateShareLinkResult;
};

export const CreateShareLinkParamsSchema = z.object({
  reportId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function createShareLink(reportId: string, body: CreateShareLinkBody): Promise<CreateShareLinkResponse> {
  const response = await api.post<CreateShareLinkResponse>(`/reports/${encodeURIComponent(reportId)}/share-links`, body);
  return response.data;
}

export type ListShareLinksResponse = {
  items: Array<ShareLink>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListShareLinksParamsSchema = z.object({
  reportId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export const ListShareLinksQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
});

export async function listShareLinks(reportId: string, query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
}): Promise<ListShareLinksResponse> {
  const response = await api.get<ListShareLinksResponse>(`/reports/${encodeURIComponent(reportId)}/share-links`, { params: query });
  return response.data;
}

export type RevokeShareLinkResponse = {
  data: RevokeShareLinkResult;
};

export const RevokeShareLinkParamsSchema = z.object({
  reportId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  shareLinkId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function revokeShareLink(reportId: string, shareLinkId: string): Promise<RevokeShareLinkResponse> {
  const response = await api.post<RevokeShareLinkResponse>(`/reports/${encodeURIComponent(reportId)}/share-links/${encodeURIComponent(shareLinkId)}/revoke`);
  return response.data;
}

export type GetSharedReportResponse = {
  data: SharedReportView;
};

export const GetSharedReportParamsSchema = z.object({
  token: z.string().min(1).max(256),
}).strict();

export async function getSharedReport(token: string): Promise<GetSharedReportResponse> {
  const response = await api.get<GetSharedReportResponse>(`/share/${encodeURIComponent(token)}`);
  return response.data;
}

export const shareLinksKeys = {
  all: ["shareLinks"] as const,
  lists: () => [...shareLinksKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...shareLinksKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...shareLinksKeys.all, 'detail'] as const,
  detail: (id: string) => [...shareLinksKeys.details(), id] as const,
};
